import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

const VERSION_CHECK_INTERVAL_MS = 2 * 60 * 1000;
const FORCE_UPDATE_QUERY_PARAM = 'forceAppUpdate';
const FORCE_UPDATE_STORAGE_KEY = 'paladin:force-app-update-banner';

function isForcedUpdateBannerEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  if (params.get(FORCE_UPDATE_QUERY_PARAM) === '1') {
    return true;
  }

  return window.localStorage.getItem(FORCE_UPDATE_STORAGE_KEY) === '1';
}

function getCurrentBundleUrl(): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const script = document.querySelector<HTMLScriptElement>(
    'script[type="module"][src*="/assets/"]',
  );
  return script?.src ?? null;
}

function getBundleUrlFromHtml(html: string): string | null {
  const match = html.match(
    /<script[^>]*type=["']module["'][^>]*src=["']([^"']*\/assets\/[^"']+)["']/i,
  );
  if (!match?.[1]) {
    return null;
  }

  return new URL(match[1], window.location.origin).toString();
}

export function AppUpdateNotifier() {
  const { t } = useTranslation();
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(isForcedUpdateBannerEnabled);

  useEffect(() => {
    if (isForcedUpdateBannerEnabled()) {
      setIsUpdateAvailable(true);
      return;
    }

    const currentBundleUrl = getCurrentBundleUrl();
    if (!currentBundleUrl) {
      return;
    }

    let isCancelled = false;

    const checkForUpdate = async () => {
      try {
        const response = await fetch(`/?v=${Date.now()}`, {
          cache: 'no-store',
          credentials: 'same-origin',
        });
        if (!response.ok) {
          return;
        }

        const html = await response.text();
        const latestBundleUrl = getBundleUrlFromHtml(html);
        if (!latestBundleUrl || isCancelled) {
          return;
        }

        if (latestBundleUrl !== currentBundleUrl) {
          setIsUpdateAvailable(true);
        }
      } catch {
        // Ignore transient network errors.
      }
    };

    void checkForUpdate();
    const interval = window.setInterval(() => void checkForUpdate(), VERSION_CHECK_INTERVAL_MS);

    return () => {
      isCancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  if (!isUpdateAvailable) {
    return null;
  }

  return (
    <div className="fixed right-4 bottom-4 z-50 max-w-md">
      <Alert className="border-primary/30 shadow-lg">
        <AlertTitle>{t('appUpdate.title')}</AlertTitle>
        <AlertDescription className="mt-2 flex flex-col gap-3">
          <span>{t('appUpdate.description')}</span>
          <div>
            <Button type="button" size="sm" onClick={() => window.location.reload()}>
              {t('appUpdate.reload')}
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}
